
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Site, Stats, FilterType, ApiExecutiveMonitoringResponse } from '../types';

const API_BASE_URL = '';

const mapApiStatus = (apiStatus: 'working' | 'problem' | 'not_working'): Site['status'] => {
    switch (apiStatus) {
        case 'working': return 'up';
        case 'problem': return 'warn';
        case 'not_working': return 'down';
        default: return 'down';
    }
};

const parseApiDate = (dateStr: string): number => {
    if (!dateStr || dateStr === 'Неизвестно') return Date.now();
    
    const match = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return Date.now();
    
    const [, day, month, year, hour, minute, second] = match;
    return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
    ).getTime();
};

const generateUrl = (siteId: string): string => {
    const hostnameMap: Record<string, string> = {
        'soft-servis': 'soft-servis.ru',
        'it-atmo': 'it-atmo.ru',
        'robot4b': 'robot4b.ru',
        'ufarobotics': 'ufarobotics.ru',
        'markirovka-ufa': 'markirovka-ufa.ru',
    };
    const hostname = hostnameMap[siteId] || `${siteId}.ru`;
    return `https://${hostname}`;
};

const buildApiUrl = (filter: FilterType): string => {
    const base = `${API_BASE_URL}/api/executive/monitoring`;
    if (filter === 'all') return base;

    const statusMap: Record<FilterType, string | null> = {
        all: null,
        up: 'working',
        warn: 'problem',
        down: 'not_working',
    };

    const apiStatus = statusMap[filter];
    return apiStatus ? `${base}?status=${apiStatus}` : base;
};

export const useDashboard = () => {
    const [sites, setSites] = useState<Site[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date>(() => new Date());
    const [filter, setFilter] = useState<FilterType>('all');

    const fetchSites = useCallback(async (currentFilter: FilterType = filter) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(buildApiUrl(currentFilter), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const apiData: ApiExecutiveMonitoringResponse = await response.json();

            if (!apiData.success || !apiData.data?.success) {
                throw new Error(apiData.message || 'Ошибка получения данных');
            }

            const mappedSites: Site[] = apiData.data.data.map(apiSite => {
                const lastCheckTs = parseApiDate(apiSite.lastCheck);
                const problemSinceTs = apiSite.problemSince 
                    ? parseApiDate(apiSite.problemSince) 
                    : undefined;

                return {
                    id: apiSite.id,
                    name: apiSite.name,
                    url: generateUrl(apiSite.id),
                    status: mapApiStatus(apiSite.status),
                    lastCheck: lastCheckTs,
                    responseTime: 0,
                    problemSince: apiSite.status !== 'working' ? problemSinceTs : undefined,
                };
            });

            setSites(mappedSites);
            setLastUpdate(new Date());

        } catch (err) {
            const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
            setError(message);
            console.error('Failed to fetch monitoring data:', err);
        } finally {
            setIsLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchSites(filter);
    }, [filter, fetchSites]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchSites(filter);
        }, 60000);

        return () => clearInterval(interval);
    }, [filter, fetchSites]);

    useEffect(() => {
        const interval = setInterval(() => {
            setLastUpdate(new Date());
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    const stats: Stats = useMemo(() => {
        const working = sites.filter(s => s.status === 'up').length;
        const problems = sites.filter(s => s.status === 'warn' || s.status === 'down').length;
        const uptime = sites.length > 0 ? ((working / sites.length) * 100).toFixed(1) : '0.0';
        
        return {
            total: sites.length,
            working,
            problems,
            uptime
        };
    }, [sites]);

    const filteredSites = useMemo(() => {
        if (filter === 'all') return sites;
        return sites.filter(site => site.status === filter);
    }, [sites, filter]);

    const activeIncidents = useMemo(() => 
        sites.filter(s => s.status !== 'up'),
    [sites]);

    const refreshData = useCallback(async () => {
        await fetchSites(filter);
    }, [fetchSites, filter]);

    return {
        sites,
        filteredSites,
        stats,
        filter,
        setFilter,
        isLoading,
        error,
        lastUpdate,
        activeIncidents,
        refreshData
    };
};